import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalData } from "../hooks/useLocalData";
import { ArrowLeft, Search, ChevronRight, Copy, CheckCircle, Clock, FileText, AlertTriangle, Trash2, MessageSquare, Send, RotateCcw } from "lucide-react";
import { addNoteMessage, resubmitNote } from "../services/teamService";
import BottomNav from "../components/BottomNav";

export default function NotesPage() {
  const navigate = useNavigate();
  const { notes, trashNote } = useLocalData();
  const [filter, setFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedThread, setExpandedThread] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const agentName = (() => {
    try { return JSON.parse(localStorage.getItem("fieldnotes_user_profile") || "{}").displayName || "Agent"; }
    catch { return "Agent"; }
  })();

  const filters = ["All", "Revisions", "Drafts", "Submitted", "Signed", "Priority Flags"];

  // Revision requested notes
  const revisionNotes = useMemo(() => notes.filter(n => n.status === "revision-requested"), [notes]);
  const signedNotes = useMemo(() => notes.filter(n => n.status === "signed"), [notes]);

  const filtered = notes
    .filter((n) => {
      if (filter === "Drafts") return n.status === "draft";
      if (filter === "Submitted") return n.status === "submitted";
      if (filter === "Revisions") return n.status === "revision-requested";
      if (filter === "Signed") return n.status === "signed";
      if (filter === "Priority Flags") return n.hasPriorityFlags;
      return true;
    })
    .filter((n) =>
      searchQuery
        ? (n.individualName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (n.date || "").includes(searchQuery)
        : true
    );

  const draftCount = notes.filter((n) => n.status === "draft").length;
  const submittedCount = notes.filter((n) => n.status === "submitted").length;
  const priorityCount = notes.filter((n) => n.hasPriorityFlags).length;
  const revisionCount = revisionNotes.length;
  const signedCount = signedNotes.length;

  const handleSendReply = async (noteId) => {
    if (!replyText.trim()) return;
    setSending(true);
    await addNoteMessage(noteId, replyText.trim(), agentName, "agent");
    setReplyText("");
    setSending(false);
  };

  const handleCopyNote = (note) => {
    // Build a text version of the note for clipboard
    const nd = note.noteData;
    let text = "";
    if (nd) {
      text += `Progress Note — ${note.individualName}\n`;
      text += `Date: ${note.date}\n`;
      text += `Service: ${note.serviceType}\n`;
      text += `Location: ${note.location}\n\n`;
      if (nd.responseToInterventions) text += `Response to Interventions:\n${nd.responseToInterventions}\n\n`;
      if (nd.additionalComments) text += `Additional Comments:\n${nd.additionalComments}\n`;
    } else {
      text = `${note.individualName} — ${note.date} — ${note.serviceType}`;
    }
    navigator.clipboard?.writeText(text);
    setCopiedId(note.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleTrashNote = () => {
    if (deleteConfirm) {
      trashNote(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="min-h-screen bg-surface-primary">
      <header
        className="sticky top-0 z-50 px-5 py-4 flex items-center justify-between"
        style={{
          background: "linear-gradient(135deg, #0f1729, #162033)",
          borderBottom: "1px solid rgba(99,179,237,0.1)",
        }}
      >
        <button className="flex items-center gap-3" onClick={() => navigate("/dashboard")}>
          <ArrowLeft size={20} className="text-gray-300" />
          <span className="text-lg font-bold text-gray-100">Notes</span>
        </button>
      </header>

      <div className="px-5 py-4 pb-28">
        {/* Revision Alert Banner */}
        {revisionCount > 0 && (
          <div className="glass-card p-3 mb-4" style={{ background: "rgba(246,173,85,0.06)", border: "1px solid rgba(246,173,85,0.2)" }}>
            <div className="flex items-center gap-2">
              <RotateCcw size={14} className="text-orange-400" />
              <span className="text-xs font-bold text-orange-400">
                {revisionCount} note{revisionCount !== 1 ? "s" : ""} need{revisionCount === 1 ? "s" : ""} revision
              </span>
              <span className="text-[10px] text-gray-500">— Supervisor requested changes</span>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="glass-card text-center py-3 px-2">
            <div className="text-xl font-extrabold text-yellow-400 leading-none">{draftCount}</div>
            <div className="text-[9px] text-gray-500 mt-1 font-semibold tracking-wider uppercase">Drafts</div>
          </div>
          <div className="glass-card text-center py-3 px-2">
            <div className="text-xl font-extrabold text-blue-400 leading-none">{submittedCount}</div>
            <div className="text-[9px] text-gray-500 mt-1 font-semibold tracking-wider uppercase">Submitted</div>
          </div>
          <div className="glass-card text-center py-3 px-2">
            <div className="text-xl font-extrabold text-green-400 leading-none">{signedCount}</div>
            <div className="text-[9px] text-gray-500 mt-1 font-semibold tracking-wider uppercase">Signed</div>
          </div>
          <div className="glass-card text-center py-3 px-2">
            <div className="text-xl font-extrabold leading-none" style={{ color: revisionCount > 0 ? "#f6ad55" : "#68d391" }}>{revisionCount}</div>
            <div className="text-[9px] text-gray-500 mt-1 font-semibold tracking-wider uppercase">Revisions</div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" />
          <input
            className="field-input pl-10"
            placeholder="Search by name or date..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className="px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors"
              style={{
                background: filter === f ? "rgba(49,130,206,0.2)" : f === "Revisions" && revisionCount > 0 ? "rgba(246,173,85,0.08)" : "rgba(15,23,42,0.5)",
                color: filter === f ? "#63b3ed" : f === "Revisions" && revisionCount > 0 ? "#f6ad55" : "#718096",
                border: `1px solid ${filter === f ? "rgba(49,130,206,0.3)" : f === "Revisions" && revisionCount > 0 ? "rgba(246,173,85,0.15)" : "rgba(99,179,237,0.06)"}`,
              }}
            >
              {f}
              {f === "Revisions" && revisionCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-orange-500/20 text-orange-400">
                  {revisionCount}
                </span>
              )}
              {f === "Drafts" && draftCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-yellow-500/20 text-yellow-400">
                  {draftCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Notes List */}
        <div className="stagger-children">
          {filtered.map((note) => (
            <div key={note.id} className="glass-card p-4 mb-3" style={{
              borderLeft: note.status === "revision-requested" ? "3px solid #f6ad55"
                : note.status === "signed" ? "3px solid #68d391"
                : note.hasPriorityFlags ? "3px solid #fc8181"
                : "3px solid transparent",
            }}>
              <div className="flex justify-between items-start">
                <div
                  className="flex-1"
                  onClick={() => {
                    if (note.status === "draft") {
                      navigate("/session/processing", {
                        state: {
                          sessionId: note.id, draftNoteId: note.id,
                          transcript: note.transcript || "",
                          individualName: note.individualName, dob: note.dob,
                          locationType: note.locationType, location: note.location,
                          serviceType: note.serviceType,
                          knownMedications: note.knownMedications || [],
                          knownDiagnoses: note.knownDiagnoses || [],
                          extractedFacts: note.extractedFacts || [],
                          duration: note.durationSeconds || 1800,
                          sessionData: { duration: note.durationSeconds || 1800, flaggedSegments: note.flaggedNotes || [] },
                        },
                      });
                    } else {
                      navigate("/session/review", {
                        state: {
                          note: note.noteData,
                          readOnly: note.status !== "revision-requested",
                          noteId: note.id,
                          isRevision: note.status === "revision-requested",
                        },
                      });
                    }
                  }}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base font-bold text-gray-100">{note.individualName}</span>
                    {note.hasPriorityFlags && (
                      <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-500/15 text-red-400 uppercase">
                        <AlertTriangle size={8} /> Priority
                      </span>
                    )}
                    {note.status === "signed" && (
                      <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-green-500/15 text-green-400">
                        <CheckCircle size={8} /> Co-Signed
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {note.date} · {note.serviceType}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {note.location} · {note.duration}
                  </div>

                  <div className="flex items-center gap-3 mt-2.5">
                    {note.status === "revision-requested" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-500/15 text-orange-400">
                        <RotateCcw size={9} /> Revision Requested
                      </span>
                    ) : note.status === "signed" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-500/10 text-green-400">
                        <CheckCircle size={9} /> Signed
                      </span>
                    ) : note.status === "submitted" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/10 text-blue-400">
                        <Clock size={9} /> Submitted
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-500/10 text-yellow-400">
                        <Clock size={9} /> Draft
                      </span>
                    )}
                    <span className="text-[10px] text-teal-400 font-semibold">
                      {note.billingCode} · {note.billingUnits}
                    </span>
                    {note.confidence && (
                      <span className="text-[10px] font-semibold" style={{
                        color: note.confidence >= 90 ? "#68d391" : note.confidence >= 80 ? "#63b3ed" : "#ecc94b",
                      }}>{note.confidence}%</span>
                    )}
                    {note.messageThread?.length > 0 && (
                      <button onClick={(e) => { e.stopPropagation(); setExpandedThread(expandedThread === note.id ? null : note.id); }}
                        className="flex items-center gap-1 text-[10px] text-blue-400 font-semibold">
                        <MessageSquare size={9} /> {note.messageThread.length}
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0 ml-2">
                  <button
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: "rgba(99,179,237,0.08)" }}
                    onClick={(e) => { e.stopPropagation(); handleCopyNote(note); }}
                  >
                    {copiedId === note.id ? (
                      <CheckCircle size={14} className="text-green-400" />
                    ) : (
                      <Copy size={14} className="text-blue-400" />
                    )}
                  </button>
                  {note.status !== "signed" && (
                    <button
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ background: "rgba(252,129,129,0.06)" }}
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm(note); }}
                    >
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  )}
                </div>
              </div>

              {/* Revision request banner */}
              {note.status === "revision-requested" && note.revisionReason && (
                <div className="mt-3 p-3 rounded-lg" style={{ background: "rgba(246,173,85,0.06)", border: "1px solid rgba(246,173,85,0.12)" }}>
                  <div className="text-[9px] font-bold text-orange-400 uppercase tracking-wider mb-1">
                    Revision requested by {note.revisionRequestedBy}
                  </div>
                  <div className="text-xs text-gray-400 italic leading-relaxed">{note.revisionReason}</div>
                  <button
                    className="mt-2 w-full py-2 rounded-lg text-xs font-bold text-center text-white"
                    style={{ background: "linear-gradient(135deg, #dd6b20, #c05621)" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/session/review", {
                        state: {
                          note: note.noteData,
                          readOnly: false,
                          noteId: note.id,
                          isRevision: true,
                          revisionReason: note.revisionReason,
                        },
                      });
                    }}
                  >
                    Edit & Resubmit
                  </button>
                </div>
              )}

              {/* Signed by info */}
              {note.status === "signed" && note.supervisorSignedBy && (
                <div className="mt-2 text-[10px] text-green-400/60">
                  Co-signed by {note.supervisorSignedBy} · {note.supervisorSignedAt?.toDate ? note.supervisorSignedAt.toDate().toLocaleDateString() : ""}
                </div>
              )}

              {/* Message thread */}
              {expandedThread === note.id && note.messageThread?.length > 0 && (
                <div className="mt-3 p-3 rounded-lg" style={{ background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.04)" }}>
                  <div className="text-[9px] font-bold text-blue-400 uppercase tracking-wider mb-2">
                    <MessageSquare size={9} className="inline mr-1" /> Thread
                  </div>
                  {note.messageThread.map((msg, i) => (
                    <div key={msg.id || i} className="mb-1 p-2 rounded-lg" style={{
                      background: msg.role === "supervisor" ? "rgba(49,130,206,0.06)" : "rgba(79,209,197,0.06)",
                      borderLeft: `2px solid ${msg.role === "supervisor" ? "#3182ce" : "#4fd1c5"}`,
                    }}>
                      <div className="flex justify-between mb-0.5">
                        <span className="text-[10px] font-bold" style={{ color: msg.role === "supervisor" ? "#63b3ed" : "#4fd1c5" }}>
                          {msg.from}
                          {msg.type === "revision-request" && <span className="text-orange-400 ml-1">· Revision Request</span>}
                          {msg.type === "signed" && <span className="text-green-400 ml-1">· Signed</span>}
                          {msg.type === "resubmission" && <span className="text-teal-400 ml-1">· Resubmitted</span>}
                        </span>
                        <span className="text-[8px] text-gray-600">
                          {new Date(msg.timestamp).toLocaleDateString()} {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-400">{msg.text}</div>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-2">
                    <input
                      className="flex-1 px-3 py-1.5 rounded-lg text-xs bg-surface-secondary text-gray-200 outline-none"
                      style={{ background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)" }}
                      placeholder="Reply..."
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleSendReply(note.id)}
                      onClick={e => e.stopPropagation()}
                    />
                    <button onClick={(e) => { e.stopPropagation(); handleSendReply(note.id); }}
                      disabled={sending || !replyText.trim()}
                      className="px-3 py-1.5 rounded-lg text-blue-400"
                      style={{ background: "rgba(49,130,206,0.15)", opacity: sending || !replyText.trim() ? 0.4 : 1 }}>
                      <Send size={12} />
                    </button>
                  </div>
                </div>
              )}

              {/* Draft generate button */}
              {note.status === "draft" && (
                <div className="flex gap-2 mt-3 pt-3" style={{ borderTop: "1px solid rgba(99,179,237,0.06)" }}>
                  <button
                    className="flex-1 py-2 rounded-lg text-xs font-semibold text-center text-white"
                    style={{ background: "linear-gradient(135deg, #3182ce, #2b6cb0)" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/session/processing", {
                        state: {
                          sessionId: note.id, draftNoteId: note.id,
                          transcript: note.transcript || "",
                          individualName: note.individualName, dob: note.dob,
                          locationType: note.locationType, location: note.location,
                          serviceType: note.serviceType,
                          knownMedications: note.knownMedications || [],
                          knownDiagnoses: note.knownDiagnoses || [],
                          extractedFacts: note.extractedFacts || [],
                          duration: note.durationSeconds || 1800,
                          sessionData: { duration: note.durationSeconds || 1800, flaggedSegments: note.flaggedNotes || [] },
                        },
                      });
                    }}
                  >
                    Generate Note Now
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Empty State */}
        {notes.length === 0 && (
          <div className="text-center mt-12">
            <FileText size={32} className="text-gray-700 mx-auto mb-3" />
            <div className="text-sm text-gray-500 mb-1">No notes yet</div>
            <div className="text-xs text-gray-600">
              Complete a session to generate your first AI-powered clinical note.
            </div>
          </div>
        )}

        {notes.length > 0 && filtered.length === 0 && (
          <div className="text-center mt-12">
            <FileText size={32} className="text-gray-700 mx-auto mb-3" />
            <div className="text-sm text-gray-500">No notes match this filter.</div>
          </div>
        )}
      </div>

      {/* ===== TRASH CONFIRM ===== */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-8">
          <div
            className="w-full max-w-sm rounded-2xl p-6"
            style={{
              background: "linear-gradient(135deg, #0f1729, #162033)",
              border: "1px solid rgba(236,201,75,0.2)",
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-yellow-400" />
              <h3 className="text-lg font-bold text-gray-100">Move Note to Trash?</h3>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-1">
              Move the {deleteConfirm.status} note for <span className="text-gray-200 font-semibold">{deleteConfirm.individualName}</span> from {deleteConfirm.date} to the trash?
            </p>
            <p className="text-xs text-gray-600 mb-5">
              It will be kept in Trash for 30 days and can be restored anytime from Settings.
            </p>
            <div className="flex gap-3">
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold"
                style={{ background: "rgba(99,179,237,0.08)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)" }}
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white"
                style={{ background: "linear-gradient(135deg, #b7791f, #975a16)" }}
                onClick={handleTrashNote}
              >
                Move to Trash
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav active="notes" />
    </div>
  );
}